import { useDatabaseContent } from '@databases/database-state';
import { setView as setViewInState } from '@databases/database-state/state/view';
import { defaultStoreUtils } from '@databases/redux';
import {
  DatabaseView,
  DEFAULT_DATABASE_VIEW_SETTINGS,
  ViewId,
} from '@databases/types/database/view';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import {
  applyViewChange,
  getDefaultView,
  ViewChangeType,
  ViewChangeTypes,
} from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import { isStandalone } from '@databases/utils/utils';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import {
  initialLayoutSettings,
  LayoutSettingsInterface,
  ViewLoader,
} from '../components/ViewLoader';
import { ViewPanelSettingType } from '../types/types';

const DEFAULT_VIEW: DatabaseView = {
  id: '',
  name: '',
  settings: DEFAULT_DATABASE_VIEW_SETTINGS,
};

export interface ViewContextInterface {
  view: DatabaseView;
  unsavedView: DatabaseView | null;
  resolvedView: DatabaseView;
  setViewChange: (delta: ViewChangeType | ViewChangeType[]) => void;
  getViewChangeList: () => ViewChangeType[];
  resetViewChangeList: (viewId: string) => void;
  setViewId: (viewId: string) => void;
  activePanelSetting: ViewPanelSettingType | null;
  setActivePanelSetting: (setting: ViewPanelSettingType | null) => void;
  clearUnsavedView: () => void;
}

export const ViewContext = createContext<ViewContextInterface>({
  view: DEFAULT_VIEW,
  unsavedView: null,
  resolvedView: DEFAULT_VIEW,
  setViewChange: () => {},
  getViewChangeList: () => [],
  resetViewChangeList: () => {},
  setViewId: () => {},
  activePanelSetting: null,
  setActivePanelSetting: () => {},
  clearUnsavedView: () => {},
});

export const ViewProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    savedViewId: urlSavedViewId,
    unsavedViewId: initialUnsavedViewId,
    contentId,
  } = useDatabaseContent();
  const [viewChangeLists, setViewChangeLists] = useState<
    Record<string, ViewChangeType[]>
  >({});
  const [activePanelSetting, setActivePanelSetting] =
    useState<ViewPanelSettingType | null>(null);

  const [unsavedViewId, setUnsavedViewId] = useState<string | null>(initialUnsavedViewId);
  const [viewId, setViewId] = useState<string | null>(urlSavedViewId);
  const [resolvedView, setResolvedView] = useState<DatabaseView>(DEFAULT_VIEW);

  const [view, setView] = useState<DatabaseView>(DEFAULT_VIEW);
  const [unsavedView, setUnsavedView] = useState<DatabaseView | null>(null);
  const [layoutSettings, setLayoutSettings] =
    useState<LayoutSettingsInterface>(initialLayoutSettings);

  const databaseDefaultViewId = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdOptional(state, {
      contentId: contentId ?? '',
    });

    if (!database || isBrokenDatabase(database)) {
      return null;
    }

    return getDefaultView(database).id;
  });

  useEffect(() => {
    if (urlSavedViewId) {
      setViewId(urlSavedViewId);
    } else {
      setViewId(databaseDefaultViewId);
    }

    if (initialUnsavedViewId) {
      if (urlSavedViewId === null || urlSavedViewId === ViewId.UNSAVED) {
        setViewId(ViewId.UNSAVED);
      }
      setUnsavedViewId(initialUnsavedViewId);
    }
  }, [urlSavedViewId, initialUnsavedViewId, setUnsavedViewId, databaseDefaultViewId]);

  const processViewChange = useCallback(
    (baseView: DatabaseView, delta: ViewChangeType) => {
      // if it's a ViewChangeTypes.LAYOUT_TYPE change
      // we need to maintain other settings for this view
      // so our base view need to get his previous state back (maintained in layoutSettings)
      if (delta.type === ViewChangeTypes.LAYOUT_TYPE) {
        baseView = {
          ...view,
          settings: { ...view.settings, layout: layoutSettings[delta.value] },
        };
      }

      return applyViewChange(baseView, delta);
    },
    [layoutSettings, view]
  );

  const setViewChange = useCallback(
    (delta: ViewChangeType | ViewChangeType[]) => {
      if (!Array.isArray(delta)) {
        delta = [delta];
      }

      let newView = view;
      delta.forEach((change) => {
        newView = processViewChange(newView, change);
      });

      setLayoutSettings((prev) => {
        return {
          ...prev,
          [newView.settings.layout.type]: newView.settings.layout,
        };
      });
      setView(newView);
      if (viewId) {
        setViewChangeLists((prevViewChangeLists) => {
          return {
            ...prevViewChangeLists,
            [viewId]: [...(prevViewChangeLists[viewId] ?? []), ...delta],
          };
        });
      }
    },
    [processViewChange, view, viewId]
  );

  const getView = useCallback(() => {
    const newViewState = {
      ...view,
      settings: { ...view.settings, layout: layoutSettings[view.settings.layout.type] },
    };
    setViewInState(newViewState);

    return newViewState;
  }, [layoutSettings, view]);

  const addViewIdToURL = useCallback((newViewId: string) => {
    if (isStandalone()) {
      const parentWindow = window.parent;
      const urlParams = new URLSearchParams(parentWindow.location.search);
      urlParams.set('savedViewId', newViewId);
      parentWindow.history.replaceState(
        {},
        '',
        `${parentWindow.location.pathname}?${urlParams}`
      );
    }
  }, []);

  const onSetViewId = useCallback(
    (newViewId: string) => {
      setViewId(newViewId);
      addViewIdToURL(newViewId);
    },
    [addViewIdToURL]
  );

  const clearUnsavedView = useCallback(() => {
    setUnsavedView(null);
  }, []);

  const resetViewChangeList = useCallback((viewId: string) => {
    setViewChangeLists((prevViewChangeLists) => {
      return {
        ...prevViewChangeLists,
        [viewId]: [],
      };
    });
  }, []);

  const currentViewChangeList = useMemo(
    () => (viewId ? viewChangeLists[viewId] ?? [] : []),
    [viewChangeLists, viewId]
  );

  return (
    <ViewContext.Provider
      value={{
        view: getView(),
        unsavedView,
        setViewChange,
        getViewChangeList: () => currentViewChangeList,
        resetViewChangeList,
        activePanelSetting,
        setActivePanelSetting,
        setViewId: onSetViewId,
        resolvedView,
        clearUnsavedView,
      }}
    >
      <ViewLoader
        setView={setView}
        viewId={viewId}
        urlSavedViewId={urlSavedViewId}
        unsavedViewId={unsavedViewId}
        unsavedView={unsavedView}
        viewChangeList={currentViewChangeList}
        setUnsavedView={setUnsavedView}
        setResolvedView={setResolvedView}
        setLayoutSettings={setLayoutSettings}
      />
      {children}
    </ViewContext.Provider>
  );
};
